import React from 'react';
import {FormattedMessage, injectIntl, IntlShape} from 'react-intl';
import BoxBody from '../common/BoxBody';
import BoxFooter from '../common/BoxFooter';
import XactFooterButton from '../common/XactFooterButton';
import ImpersonationEntry from '../../models/ImpersonationEntry';
import {onEnterKeyPress} from '../../utils/enter-key-listener';
import ImpersonationSettings from '../../models/ImpersonationSettings';
import {ImpersonationChoice} from '../../openam/OpenAmMessage';

interface ImpersonationModeFormProps {
  intl: IntlShape;
  currentEntityOuId: string;
  currentResource: string;
  currentOuId: string;
  entityOuList: ImpersonationEntry[];
  ouList: ImpersonationEntry[];
  userList: ImpersonationEntry[];
  handleImpersonationStep: (settings: ImpersonationSettings) => void;
}

interface ImpersonationModeFormState {
  settings: ImpersonationSettings;
  ouQuery: string;
  ouFiltering: boolean;
  ouMenuOpen: boolean;
  ouHighlightIndex: number;
}

class ImpersonationModeForm extends React.Component<
  ImpersonationModeFormProps,
  ImpersonationModeFormState
> {
  static MILLISECONDS_TO_WAIT_FROM_LAST_KEY_UP: number = 1000;
  private typingTimeout: any = null;

  constructor(props: ImpersonationModeFormProps) {
    super(props);
    const user =
      props.userList.length > 1 // If there is full list, do not select default user
        ? undefined
        : this.getDefaultImpersonationUser();
    const selectedOu = props.ouList.find(ou => ou.id === props.currentOuId);
    this.state = {
      settings: {
        entityOuId: props.currentEntityOuId,
        resourceId: props.currentResource,
        ouId: props.currentOuId,
        userId: user?.id,
        loadFullUserList: false,
        impersonationChoice: ImpersonationChoice.REFRESH,
      },
      ouQuery: selectedOu?.name || '',
      ouFiltering: false,
      ouMenuOpen: false,
      ouHighlightIndex: 0,
    };
  }

  getDefaultImpersonationUser = (): ImpersonationEntry | undefined => {
    return (
      this.props.userList.find(user =>
        user.name.startsWith('Default OU User Impersonation'),
      ) || this.props.userList[0]
    );
  };

  updateSettings = (
    updatedFields: Partial<ImpersonationSettings>,
    callback?: () => void,
  ) => {
    this.setState(
      prevState => ({
        settings: { ...prevState.settings, ...updatedFields },
      }),
      callback,
    );
  };

  componentWillUnmount() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }

  handleEntityOuChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const entityOuName = event.target.value;
    const entityOu = this.props.entityOuList.find(ou => ou.name === entityOuName);
    this.updateSettings(
      {
        entityOuId: entityOu?.id || '',
        resourceId: '',
        ouId: '',
        userId: '',
        loadFullUserList: false,
        impersonationChoice: ImpersonationChoice.REFRESH,
      },
      () => this.props.handleImpersonationStep(this.state.settings),
    );
  };

  handleResourceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const resourceId = event.target.value;
    this.updateSettings({
      resourceId: resourceId,
      ouId: '',
      userId: '',
      loadFullUserList: false,
      impersonationChoice: ImpersonationChoice.REFRESH,
    });
  };

  handleResourceSelected = () => {
    this.props.handleImpersonationStep(this.state.settings);
  };

  getFilteredOus = (): ImpersonationEntry[] => {
    if (!this.state.ouFiltering) {
      return this.props.ouList;
    }
    const query = this.state.ouQuery.trim().toLowerCase();
    if (!query) {
      return this.props.ouList;
    }
    return this.props.ouList.filter(ou => ou.name.toLowerCase().includes(query));
  };

  selectOu = (ou: ImpersonationEntry) => {
    if (ou.id === this.state.settings.ouId) {
      this.setState({
        ouQuery: ou.name,
        ouFiltering: false,
        ouMenuOpen: false,
        ouHighlightIndex: 0,
      });
      return;
    }
    this.setState(
      prevState => ({
        ouQuery: ou.name,
        ouFiltering: false,
        ouMenuOpen: false,
        ouHighlightIndex: 0,
        settings: {
          ...prevState.settings,
          ouId: ou.id,
          userId: '',
          loadFullUserList: false,
          impersonationChoice: ImpersonationChoice.REFRESH,
        },
      }),
      () => this.props.handleImpersonationStep(this.state.settings),
    );
  };

  handleOuQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const ouQuery = event.target.value;
    const match = this.props.ouList.find(
      ou => ou.name.toLowerCase() === ouQuery.trim().toLowerCase(),
    );
    if (match) {
      this.selectOu(match);
      return;
    }
    this.setState(prevState => ({
      ouQuery,
      ouFiltering: true,
      ouMenuOpen: true,
      ouHighlightIndex: 0,
      settings: {
        ...prevState.settings,
        ouId: '',
        userId: '',
      },
    }));
  };

  handleOuFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
    this.setState({ ouMenuOpen: true, ouFiltering: false, ouHighlightIndex: 0 });
  };

  handleOuBlur = () => {
    const selectedOu = this.props.ouList.find(
      ou => ou.id === this.state.settings.ouId,
    );
    this.setState({
      ouMenuOpen: false,
      ouFiltering: false,
      ouQuery: selectedOu?.name || '',
    });
  };

  handleOuKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const filteredOus = this.getFilteredOus();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState(prevState => ({
        ouMenuOpen: true,
        ouHighlightIndex:
          filteredOus.length === 0
            ? 0
            : Math.min(prevState.ouHighlightIndex + 1, filteredOus.length - 1),
      }));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setState(prevState => ({
        ouMenuOpen: true,
        ouHighlightIndex: Math.max(prevState.ouHighlightIndex - 1, 0),
      }));
      return;
    }
    if (event.key === 'Escape') {
      this.handleOuBlur();
      return;
    }
    if (event.key !== 'Enter' || filteredOus.length === 0) {
      return;
    }
    event.preventDefault();
    const exact = this.props.ouList.find(
      ou => ou.name.toLowerCase() === this.state.ouQuery.trim().toLowerCase(),
    );
    const highlighted = this.state.ouFiltering
      ? filteredOus[this.state.ouHighlightIndex]
      : undefined;
    const chosen = exact || (filteredOus.length === 1 ? filteredOus[0] : highlighted);
    if (chosen) {
      this.selectOu(chosen);
    }
  };

  handleUserChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.updateSettings({
      userId: event.target.value,
    });
  };

  handleLoadFullUserList = () => {
    if (this.props.userList.length === 1 && !this.state.settings.loadFullUserList) {
      this.updateSettings(
        {
          loadFullUserList: true,
          impersonationChoice: ImpersonationChoice.REFRESH,
        },
        () => this.props.handleImpersonationStep(this.state.settings),
      );
    }
  };

  isImpersonationSettingsValid = (): boolean =>
    !!this.state.settings.userId &&
    !!this.state.settings.ouId &&
    !!this.state.settings.entityOuId;

  handleStartImpersonation = () => {
    if (this.isImpersonationSettingsValid()) {
      this.updateSettings(
        {
          impersonationChoice: ImpersonationChoice.IMPERSONATE,
        },
        () => this.props.handleImpersonationStep(this.state.settings),
      );
    }
  };

  handleImpersonationSkip = () => {
    this.updateSettings(
      {
        impersonationChoice: ImpersonationChoice.SKIP,
      },
      () => this.props.handleImpersonationStep(this.state.settings),
    );
  };

  onKeyUpEvent = () => {
    clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      this.handleResourceSelected();
    }, ImpersonationModeForm.MILLISECONDS_TO_WAIT_FROM_LAST_KEY_UP);
  };

  render() {
    const selectedEntityOu = this.props.entityOuList.find(
      ou => ou.id === this.state.settings.entityOuId,
    );
    const selectedUser = this.props.userList.find(
      user => user.id === this.state.settings.userId,
    );
    const filteredOus = this.getFilteredOus();
    return (
      <>
        <BoxBody>
          <h4>
            <FormattedMessage id="impersonation.title" />
          </h4>
          <label className="m-0" htmlFor="entityOu">
            <FormattedMessage id="impersonation.entity.ou.name.label" />
          </label>
          <select
            id="entityOu"
            className="input-field"
            autoComplete="off"
            value={selectedEntityOu?.name}
            onChange={this.handleEntityOuChange}
          >
            {this.props.entityOuList.map(entityOu => (
              <option key={entityOu.id} value={entityOu.name}>
                {entityOu.name}
              </option>
            ))}
          </select>
          <label className="m-0" htmlFor="resource">
            <FormattedMessage id="impersonation.resource.label" />
          </label>
          <input
            type="text"
            className="input-field"
            id="resource"
            onChange={this.handleResourceChange}
            onKeyUp={_ => this.onKeyUpEvent()}
            onKeyDown={event => {
              clearTimeout(this.typingTimeout);
              onEnterKeyPress(event, this.handleResourceSelected);
            }}
            value={this.state.settings.resourceId}
          />
          <label className="m-0" htmlFor="ou">
            <FormattedMessage id="impersonation.ou.name.label" />
          </label>
          <div className="ou-combobox">
            <input
              id="ou"
              type="text"
              className="input-field ou-combobox-input"
              autoComplete="off"
              role="combobox"
              aria-expanded={this.state.ouMenuOpen}
              aria-controls="ou-listbox"
              aria-autocomplete="list"
              placeholder={this.props.intl.formatMessage({
                id: 'impersonation.select',
              })}
              value={this.state.ouQuery}
              onChange={this.handleOuQueryChange}
              onFocus={this.handleOuFocus}
              onBlur={this.handleOuBlur}
              onKeyDown={this.handleOuKeyDown}
            />
            {this.state.ouMenuOpen && filteredOus.length > 0 && (
              <ul id="ou-listbox" className="ou-combobox-list" role="listbox">
                {filteredOus.map((ou, index) => (
                  <li key={ou.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={ou.id === this.state.settings.ouId}
                      className={
                        index === this.state.ouHighlightIndex ? 'active' : undefined
                      }
                      onMouseDown={event => {
                        event.preventDefault();
                        this.selectOu(ou);
                      }}
                      onMouseEnter={() => this.setState({ ouHighlightIndex: index })}
                    >
                      {ou.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label
            className="m-0"
            htmlFor="user"
            title={this.props.intl.formatMessage({
              id: 'impersonation.user.select.info',
              defaultMessage: '',
            })}
          >
            <FormattedMessage id="impersonation.user.name.label" />
          </label>
          <select
            id="user"
            className="input-field"
            disabled={this.props.userList.length === 0}
            value={selectedUser?.id || ''}
            onChange={this.handleUserChange}
            onFocus={this.handleLoadFullUserList}
          >
            <option value="">
              {this.props.intl.formatMessage({ id: 'impersonation.select' })}
            </option>
            {this.props.userList.map(user => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </BoxBody>
        <BoxFooter>
          <div className="col-auto mr-auto">
            <div className="row align-items-center">
              <XactFooterButton
                id="skip-impersonation-mode-button"
                onClick={this.handleImpersonationSkip}
                i18nKey="impersonation.skip"
                autoFocus={true}
              />
            </div>
          </div>
          <XactFooterButton
            id="start-impersonation-mode-button"
            onClick={this.handleStartImpersonation}
            i18nKey="impersonation.start"
            disabled={!this.isImpersonationSettingsValid()}
          />
        </BoxFooter>
      </>
    );
  }
}

export default injectIntl(ImpersonationModeForm);
